import { useState } from 'react';
import { Alert, Skeleton } from 'antd';
import PropTypes, { bool, func } from 'prop-types';
import { observer } from 'mobx-react';

import { ModalCustom } from '$src/components/Modal';
import useModalEvents from '$src/hooks/useModalEvents';
import { INDOOR_DEVICE_ACKNOWLEDGEMENT } from '$src/constants/actions';
import { deviceType, userType } from '$src/types';
import DeviceSection from './DeviceSection';
import "./index.less";

function DevicesList({
  warningOnToggleLastDevice,
  subscribedDevices,
  handleSubscribe,
  notEligibleDevices,
  eligibleIndoorDevices,
  eligibleOutdoorDevices,
  loading,
  user,
  sendStats,
}) {
  const [deviceCheck, setDeviceCheck] = useState(null);

  const [handleModalClick, showModal] = useModalEvents();
  const [handleIndoorModalClick, showIndoorModal] = useModalEvents();

  const isLastSelectedDevice = subscribedDevices.length === 1;

  const manageSubscription = async (doorbotId, checked) => {
    const selectedDevice = [...eligibleOutdoorDevices, ...eligibleIndoorDevices].find((d) => d.doorbotId === doorbotId);
    await handleSubscribe(selectedDevice, checked);
    setDeviceCheck(null);
  };

  const handleChange = (isIndoor) => (checked, doorbotId) => {
    setDeviceCheck(doorbotId);
    if (warningOnToggleLastDevice && !checked && isLastSelectedDevice) {
      handleModalClick();
    } else if (isIndoor && checked) {
      handleIndoorModalClick();
    } else {
      manageSubscription(doorbotId, checked);
    }
  };

  const closeModalAndUnsuscribe = () => {
    handleModalClick();
    manageSubscription(deviceCheck, false);
  };

  const closeIndoorDeviceModalAndSuscribe = () => {
    handleIndoorModalClick();
    manageSubscription(deviceCheck, true);
    sendStats(INDOOR_DEVICE_ACKNOWLEDGEMENT, deviceCheck);
  };

  return (
    <>
      <Skeleton active title paragraph={{ rows: 4 }} loading={loading}>
        <DeviceSection
          title="Eligible Outdoor Devices"
          type="toogle"
          deviceCheck={deviceCheck}
          data={eligibleOutdoorDevices}
          handleChange={handleChange()}
          data-testid="elegible-outdoor-devices"
        >
          {eligibleOutdoorDevices?.length === 0 && <Alert className="alert-no-devices" message="No Eligible Outdoor Devices" />}
        </DeviceSection>
        <DeviceSection
          title="Eligible Indoor Devices"
          type="toogle"
          deviceCheck={deviceCheck}
          data={eligibleIndoorDevices}
          handleChange={handleChange(true)}
          data-testid="elegible-indoor-devices"
        >
          {eligibleIndoorDevices.length === 0 && <Alert className="alert-no-devices" message="No Eligible Indoor Devices" />}
        </DeviceSection>
        {notEligibleDevices.length > 0 && (
          <DeviceSection
            title="Ineligible Devices"
            subTitle="These devices are not currently eligible for Virtual Security Guard. See below for more details."
            type="plain"
            data={notEligibleDevices}
            notEligible
            data-testid="ineligible-devices"
          />
        )}
      </Skeleton>
      <ModalCustom
        title="No Authorized Devices"
        data-testid="no-authorized-devices-modal"
        contentTitle="This location has an active Virtual Security Guard Subscription"
        icon="not_monitorable.svg"
        width="650px"
        contentMessage="At least one authorized device is required for agents to review events on your property."
        visible={showModal}
        handleCancel={() => {
          setDeviceCheck(null);
        }}
        setModalVisible={handleModalClick}
        handleOk={closeModalAndUnsuscribe}
        cancelOnX
        okText="Deauthorize Device"
        cancelText="Cancel"
      />
      <ModalCustom
        title="Indoor Device"
        contentTitle="Please confirm you’d like to enroll an indoor device in Virtual Security Guard."
        icon="stickup_cam_encrypted.svg"
        width="650px"
        contentMessage={
          <>
            <p>
              {' '}
              When the service is active, Guards will monitor motion events on your enrolled cameras when your system is in Home or Away Mode. You can
              disable indoor monitoring while your system is in Home Mode. To do this, go to Modes settings, select Home, and disable Motion Detection
              and Live View for your indoor cameras.{' '}
              <a target="_blank" rel="noreferrer" href="https://support.ring.com/hc/en-us/articles/10427396623892">
                Learn More
              </a>
            </p>
            <b>To protect your privacy, Guards are trained to unenroll cameras that face bedrooms and bathrooms.</b>
            <br />
            <br />
          </>
        }
        visible={showIndoorModal}
        handleCancel={() => {
          setDeviceCheck(null);
        }}
        cancelOnX
        handleOk={closeIndoorDeviceModalAndSuscribe}
        okText="Enroll Indoor Device"
        cancelText="Cancel"
        enableShowAgain
        showAgainStorageKey={`indoorDeviceAcknowledgement-${user.user_id}`}
        setModalVisible={handleIndoorModalClick}
      />
    </>
  );
}

DevicesList.propTypes = {
  hasSubscribedDevices: func,
  warningOnToggleLastDevice: bool,
  subscribedDevices: PropTypes.arrayOf(deviceType),
  notEligibleDevices: PropTypes.arrayOf(deviceType),
  eligibleIndoorDevices: PropTypes.arrayOf(deviceType),
  eligibleOutdoorDevices: PropTypes.arrayOf(deviceType),
  loading: bool,
  user: userType,
  sendStats: func,
  handleSubscribe: func,
};

export default observer(DevicesList);
